import React from 'react'
import clsx from 'clsx'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import LeftPanelCloseIcon from 'components/material-symbol/icons/LeftPanelClose'
import LeftPanelOpenIcon from 'components/material-symbol/icons/LeftPanelOpen'
import SidebarItem, { SidebarItemShell } from './sidebar-item'
import { drawerWidth, desktopRailWidth, collapsedDrawerWidth } from './styles'
import { useIsDesktopNav, useIsCollapsibleSidebar } from 'lib/hooks'

// Drawer slot styling lives here as a makeStyles ruleset (rather than a
// styled() definition). MUI v4 in development mode generates hashed
// class names (e.g. `MuiDrawer-paper-1811`), so descendant selectors
// like `& .MuiDrawer-paper` do not match. The canonical MUI v4 way to
// style slots is via the component's `classes` prop, which is what we
// do here. When MUI v5/v6 lands, this can migrate to slot-aware
// `styled()` definitions in one place.
const reducedMotion = '@media (prefers-reduced-motion: reduce)'

const useDrawerStyles = makeStyles(theme => ({
  drawerContainer: ({ sidebarLayoutWidth }) => ({
    flexShrink: 0,
    ...(sidebarLayoutWidth > 0 && {
      width: sidebarLayoutWidth,
      whiteSpace: 'nowrap',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shorter,
      }),
      [reducedMotion]: { transition: 'none' },
    }),
  }),
  drawerPaper: ({ assumedUser }) => ({
    width: drawerWidth,
    borderRight: '1px solid #E0E0E0',
    borderRadius: 0,
    // MUI's paper is fixed at top: 0 with height: 100%, so the impersonation
    // offset has to come off the height or the bottom of the paper (and the
    // collapse toggle) falls below the viewport.
    marginTop: assumedUser ? '30px' : 'unset',
    height: assumedUser ? 'calc(100% - 30px)' : '100%',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
      height: assumedUser ? 'calc(100% - 70px)' : '100%',
    },
  }),
  // Rail <-> full-width states for the persistent (desktop) and permanent
  // (tablet) drawers. The paper's width animates; item geometry is fixed so
  // labels clip instead of reflowing.
  drawerSizing: ({ paperWidth }) => ({
    width: paperWidth,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    whiteSpace: 'normal',
    transition: theme.transitions.create(['width', 'box-shadow'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    [reducedMotion]: { transition: 'none' },
  }),
  drawerPeeking: {
    boxShadow: theme.shadows[8],
  },
  menu: {
    padding: 0,
    paddingTop: theme.spacing(1.5),
  },
  toggleArea: {
    marginTop: 'auto',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1.5),
  },
  toolbarSpacing: {
    ...theme.mixins.toolbar,
    minHeight: '61px',
  },
}))

export default function Sidebar({
  menuItems,
  isMobileNavOpen,
  isTabletNavOpen,
  setIsTabletNavOpen,
  handleDrawerToggle,
  assumedUser,
  drawerContainer,
  location,
  sidebarLayoutWidth = 0,
  showNavLabels = true,
  isNavCollapsed = false,
  isPeeking = false,
  peekHandlers,
  onToggleNavCollapsed,
}) {
  const isDesktopNav = useIsDesktopNav()
  const isCollapsibleSidebar = useIsCollapsibleSidebar()
  const isMobileNav = !isDesktopNav && !isCollapsibleSidebar
  const collapsed = !showNavLabels
  const theme = useTheme()
  const railWidth = isDesktopNav
    ? desktopRailWidth
    : collapsedDrawerWidth(theme)
  const paperWidth = isDesktopNav
    ? isNavCollapsed && !isPeeking
      ? desktopRailWidth
      : drawerWidth
    : sidebarLayoutWidth
  const classes = useDrawerStyles({
    assumedUser,
    sidebarLayoutWidth,
    paperWidth,
  })

  if (!menuItems.length) return null

  const renderMenu = () => (
    <List className={classes.menu}>
      {menuItems.map(item => {
        const ItemComponent = item?.component ? item.component : SidebarItem
        return (
          <ItemComponent
            {...{
              onClick: isMobileNav ? handleDrawerToggle : undefined,
              key: item.path || item.label,
              ...item,
              location,
              isCollapsibleSidebar,
              isNavCollapsed,
              showNavLabels,
              isTabletNavOpen: isTabletNavOpen || isPeeking,
              setIsTabletNavOpen,
              collapsed,
              railWidth,
            }}
          />
        )
      })}
    </List>
  )

  if (isDesktopNav) {
    const toggleLabel = isNavCollapsed ? 'Expand' : 'Collapse'
    return (
      <nav
        className={classes.drawerContainer}
        aria-label="Main"
        data-nav-state={
          !isNavCollapsed ? 'expanded' : isPeeking ? 'peeking' : 'collapsed'
        }
      >
        <Drawer
          color="primary"
          variant="permanent"
          PaperProps={peekHandlers}
          classes={{
            paper: clsx(classes.drawerPaper, classes.drawerSizing, {
              [classes.drawerPeeking]: isNavCollapsed && isPeeking,
            }),
          }}
        >
          <div className={classes.toolbarSpacing} />
          {renderMenu()}
          {onToggleNavCollapsed && (
            <div className={classes.toggleArea}>
              <SidebarItemShell
                icon={
                  isNavCollapsed ? (
                    <LeftPanelOpenIcon />
                  ) : (
                    <LeftPanelCloseIcon />
                  )
                }
                label={toggleLabel}
                tooltip={`${toggleLabel} navigation`}
                collapsed={collapsed}
                railWidth={railWidth}
                onClick={onToggleNavCollapsed}
                aria-label={`${toggleLabel} navigation`}
                aria-expanded={!isNavCollapsed}
              />
            </div>
          )}
        </Drawer>
      </nav>
    )
  }

  if (isMobileNav) {
    return (
      <nav>
        <Drawer
          container={drawerContainer}
          variant="temporary"
          anchor="left"
          open={isMobileNavOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {renderMenu()}
        </Drawer>
      </nav>
    )
  }

  return (
    <nav className={classes.drawerContainer}>
      <Drawer
        variant="permanent"
        open={isTabletNavOpen}
        classes={{
          paper: clsx(classes.drawerPaper, classes.drawerSizing),
        }}
      >
        <div className={classes.toolbarSpacing} />
        {renderMenu()}
      </Drawer>
    </nav>
  )
}
